import React, { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Grid2 as Grid, Typography, Paper, Button, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { useStressTestRanges } from '../../../hooks/useClinicSettings';
import { useStressTests, useStressTest } from '../../../hooks/useStressTest';
import { generateStressTestPDF } from './stressTestPdfGenerator';
import { MainCard } from 'component/cards/MainCard2';

const SectionCard = ({ title, children }) => (
    <Paper
        sx={{
            p: 2,
            mb: 2,
            borderRadius: 2,
            border: '1px solid #ddd'
        }}
    >
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
            {title}
        </Typography>
        {children}
    </Paper>
);

const FieldCard = ({ label, value, background, expanded = false }) => (
    <Paper
        variant="outlined"
        sx={{
            p: 1.5,
            borderRadius: 2,
            borderColor: '#d0d0d0',
            backgroundColor: background || 'white',
            minHeight: expanded ? 100 : '70px',
            display: expanded ? 'flex' : 'block',
            flexDirection: expanded ? 'column' : 'initial',
            justifyContent: expanded ? 'space-between' : 'initial'
        }}
    >
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
            {label}
        </Typography>

        {expanded ? (
            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
                <Paper sx={{ p: 0.5 }}>
                    <Typography sx={{ textAlign: 'center' }}>{value || '—'}</Typography>
                </Paper>
            </Box>
        ) : (
            <Typography sx={{ mt: 0.5 }}>{value || '—'}</Typography>
        )}
    </Paper>
);

const FieldCardCompare = ({
    label,
    value,
    compareValue,
    background,
    compareBackground
}) => {
    const { t } = useTranslation();

    return (
        <Paper
            variant="outlined"
            sx={{
                p: 1.5,
                borderRadius: 2,
                minHeight: '80px'
            }}
        >
            <Typography variant="caption" fontWeight={600} gutterBottom>
                {label}
            </Typography>

            <Grid container spacing={1}>
                {/* Headers */}
                <Grid size={6}>
                    <Typography variant="caption" color="text.secondary">
                        {t('stress.current')}
                    </Typography>
                </Grid>
                <Grid size={6}>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textAlign: 'right' }}
                    >
                        {t('stress.selected')}
                    </Typography>
                </Grid>

                {/* Values */}
                <Grid size={6}>
                    <Paper
                        sx={{
                            p: 0.5,
                            backgroundColor: background || 'white'
                        }}
                    >
                        <Typography sx={{ textAlign: 'center' }}>
                            {value || '—'}
                        </Typography>
                    </Paper>
                </Grid>

                <Grid size={6}>
                    <Paper
                        sx={{
                            p: 0.5,
                            border: '2px solid #1976d2',
                            backgroundColor: compareBackground || 'white'
                        }}
                    >
                        <Typography sx={{ textAlign: 'center' }}>
                            {compareValue || '—'}
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>
        </Paper>
    );
};

const COMPARABLE_IDENT_FIELDS = ['weight', 'height', 'bmi', 'age'];
const COMPARABLE_GENERAL_FIELDS = ['origin', 'doctor', 'operators'];

const MEDICATION_GROUPS = [
    {
        key: 'betaBlocker',
        labelKey: 'stress.betablockers',
        typeLabelKey: 'stress.betablockersType'
    },
    {
        key: 'calciumBlocker',
        labelKey: 'stress.calciumblockers',
        typeLabelKey: 'stress.calciumblockersType'
    },
    {
        key: 'digitalis',
        labelKey: 'stress.digitalis',
        typeLabelKey: 'stress.digitalisType'
    },
    {
        key: 'antiarrhythmic',
        labelKey: 'stress.antiarrhythmic',
        typeLabelKey: 'stress.antiarrhythmicType'
    }
];

const ViewStressTest = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const { from, seeUserState } = location.state || {};
    const { uuid } = useParams();

    const { data: stressTest, isLoading, error } = useStressTest(uuid);

    const patientUuid = stressTest?.patient?.uuid;
    const { data: allTests } = useStressTests(patientUuid);

    const ranges = useStressTestRanges();

    const [selectCompareOpen, setSelectCompareOpen] = useState(false);
    const [compareTest, setCompareTest] = useState(null);

    const compareMode = Boolean(compareTest);

    if (isLoading) {
        return <Typography>{t('stress.loading')}</Typography>;
    }

    if (error || !stressTest) {
        return <Typography>{t('stress.loadingError')}</Typography>;
    }

    const file = stressTest.files?.[0];

    const testType = stressTest.type;
    const {
        testProtocolType,
        identification,
        generalData,
        clinicalInfo,
        medication,
        results,
        report
    } = stressTest.data;

    const getProtocolLabel = (protocolType) => {
        if (protocolType === 'TAPETE_ROLANTE') return t('stress.roler');
        if (protocolType === 'CICLOERGOMETRO') return t('stress.bike');
        return '—';
    };

    const compatibleTests = allTests?.filter(
        (t) => t.type === testType && t.uuid !== stressTest.uuid
    );

    const RESULT_FIELDS =
        testType === 'STRESS_TEST_CP'
            ? [
                  {
                      title: 'PE Clássica',
                      fields: [
                          'Protocolo',
                          'Tempo',
                          'Suspensão por',
                          'PA repouso',
                          'PA esforço',
                          'Extrassístoles V',
                          'Angina',
                          'Alt. Seg. ST',
                          'METS'
                      ]
                  },
                  {
                      title: 'PERC',
                      fields: [
                          'VO2',
                          'Pico',
                          '% previsto',
                          'Repouso',
                          'VT1',
                          'VT1%picoVO2',
                          'VT2',
                          'Quociente Resp.',
                          'O2 pulso % prev.'
                      ]
                  },
                  {
                      title: 'Ventilação',
                      fields: [
                          'FVC',
                          'FEV1',
                          'FEV1/FVC',
                          'Res. Respiratória',
                          'Osc. Ventilatória',
                          'VE/VCO2',
                          'EqCO2 VT1',
                          'PETCO2 VT1',
                          'ΔPETCO2'
                      ]
                  },
                  {
                      title: 'Cronotropia e Treino',
                      fields: [
                          'Repouso',
                          'VT1',
                          'VT2',
                          'FCMPI',
                          'Pico FC',
                          '% FC prevista',
                          '1º min recup',
                          '3º min recup',
                          'Índice cronotrópico'
                      ]
                  }
              ]
            : [
                  {
                      title: 'Sub-secção 1',
                      fields: [
                          'Protocolo',
                          'Tempo',
                          'Suspensão por',
                          'PAS repouso',
                          'PAS esforço'
                      ]
                  },
                  {
                      title: 'Sub-secção 2',
                      fields: [
                          'Repouso',
                          'Pico FC',
                          'FCMPI',
                          '% FC prevista',
                          'Índice cronotrópico'
                      ]
                  },
                  {
                      title: 'Sub-secção 3',
                      fields: [
                          'Extrassístoles SV',
                          'Extrassístoles V',
                          'Angina',
                          'Alt. Seg. ST'
                      ]
                  },
                  {
                      title: 'Sub-secção 4',
                      fields: ['METS', '1º min Recup', '3º min Recup', '6º min Recup']
                  }
              ];

    const getFieldColor = (field, value) => {
        if (!value || !ranges?.[field]) return 'white';

        const num = Number(value);
        if (isNaN(num)) return 'white';

        const { normal, mild, severe } = ranges[field];

        if (num >= normal.min && num <= normal.max) return '#abffae';
        if (num >= mild.min && num <= mild.max) return '#fff594';
        if (num >= severe.min && num <= severe.max) return '#fb9fae';

        return 'white';
    };

    const handleBack = () => {
        if (from) {
            navigate(from.pathname, {
                state: {
                    ...from.state,
                    restoredState: seeUserState
                }
            });
        } else {
            navigate(-1);
        }
    };

    return (
        <MainCard title={t('stress.title2')}>
            {selectCompareOpen && (
                <Paper sx={{ p: 2, mb: 2, border: '1px dashed #1976d2' }}>
                    <Typography variant="subtitle1" fontWeight={600} mb={1}>
                        {t('stress.selectTests')}
                    </Typography>

                    <Grid container spacing={2}>
                        {compatibleTests.map((test) => (
                            <Grid key={test.uuid} size={{ xs: 12, md: 4 }}>
                                <Button
                                    fullWidth
                                    variant={
                                        compareTest?.uuid === test.uuid
                                            ? 'contained'
                                            : 'outlined'
                                    }
                                    onClick={() => {
                                        setCompareTest(test);
                                    }}
                                >
                                    {test.patient.name} —{' '}
                                    {dayjs(test.ts).format('YYYY-MM-DD')}
                                </Button>
                            </Grid>
                        ))}
                    </Grid>
                </Paper>
            )}
            <Grid container spacing={2}>
                {/* Tipo de Protocolo */}
                <Grid size={12}>
                    <SectionCard title={t('stress.protocolType')}>
                        <Grid container spacing={2}>
                            {/* Protocolo atual */}
                            <Grid size={compareTest ? 6 : 12}>
                                <FieldCard
                                    label={t('stress.protocol')}
                                    value={getProtocolLabel(testProtocolType)}
                                    expanded={compareTest ? true : false}
                                    background="#fafafa"
                                />
                            </Grid>

                            {/* Protocolo da prova selecionada */}
                            {compareTest && (
                                <Grid size={6}>
                                    <FieldCard
                                        label={t('stress.protocolSelected')}
                                        value={
                                            compareTest?.data?.testProtocolType
                                                ? getProtocolLabel(
                                                      compareTest.data.testProtocolType
                                                  )
                                                : '—'
                                        }
                                        expanded={true}
                                        background="#fafafa"
                                        sx={{
                                            border: '2px solid #1976d2'
                                        }}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </SectionCard>
                </Grid>
                {/* IDENTIFICAÇÃO */}
                <Grid size={12}>
                    <SectionCard title={t('stress.identification')}>
                        <Grid container spacing={2}>
                            {Object.entries(identification).map(([key, value]) => {
                                const canCompare = COMPARABLE_IDENT_FIELDS.includes(key);

                                return (
                                    <Grid key={key} size={4}>
                                        {compareMode && canCompare ? (
                                            <FieldCardCompare
                                                label={key}
                                                value={value}
                                                compareValue={
                                                    compareTest?.data?.identification?.[
                                                        key
                                                    ]
                                                }
                                            />
                                        ) : (
                                            <FieldCard
                                                label={key}
                                                value={value}
                                                expanded={compareMode}
                                            />
                                        )}
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </SectionCard>
                </Grid>

                {/* DADOS GERAIS */}
                <Grid size={12}>
                    <SectionCard title={t('stress.data')}>
                        <Grid container spacing={2}>
                            {Object.entries(generalData).map(([key, value]) => {
                                const canCompare =
                                    COMPARABLE_GENERAL_FIELDS.includes(key);

                                return (
                                    <Grid key={key} size={4}>
                                        {compareMode && compareTest && canCompare ? (
                                            <FieldCardCompare
                                                label={key}
                                                value={value}
                                                compareValue={
                                                    compareTest?.data?.generalData?.[key]
                                                }
                                            />
                                        ) : (
                                            <FieldCard
                                                label={key}
                                                value={value}
                                                expanded={compareMode}
                                            />
                                        )}
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </SectionCard>
                </Grid>

                {/* INFO CLÍNICA */}
                <Grid size={12}>
                    <SectionCard title={t('stress.info')}>
                        <Grid container spacing={2}>
                            {/* Indicação atual */}
                            <Grid size={compareTest ? 6 : 12}>
                                <FieldCard
                                    label={t('stress.indication')}
                                    value={clinicalInfo}
                                    expanded={compareTest ? true : false}
                                    background="#fafafa"
                                />
                            </Grid>

                            {/* Indicação da prova selecionada */}
                            {compareTest && (
                                <Grid size={6}>
                                    <FieldCard
                                        label={t('stress.indicationSelected')}
                                        value={compareTest.data?.clinicalInfo || '—'}
                                        expanded={true}
                                        background="#fafafa"
                                        sx={{
                                            border: '2px solid #1976d2'
                                        }}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </SectionCard>
                </Grid>

                {/* MEDICAÇÃO */}
                <Grid size={12}>
                    <SectionCard title={t('stress.medication')}>
                        <Grid container spacing={2}>
                            {MEDICATION_GROUPS.map((group) => {
                                const current = medication?.[group.key];
                                const compare =
                                    compareTest?.data?.medication?.[group.key];

                                return (
                                    <Grid key={group.key} size={{ xs: 12, sm: 6, md: 3 }}>
                                        {/* Sim / Não */}
                                        {compareMode && compareTest ? (
                                            <FieldCardCompare
                                                label={t(group.labelKey)}
                                                value={
                                                    current?.enabled
                                                        ? t('stress.yes')
                                                        : t('stress.no')
                                                }
                                                compareValue={
                                                    compare?.enabled
                                                        ? t('stress.yes')
                                                        : t('stress.no')
                                                }
                                            />
                                        ) : (
                                            <FieldCard
                                                label={t(group.labelKey)}
                                                value={
                                                    current?.enabled
                                                        ? t('stress.yes')
                                                        : t('stress.no')
                                                }
                                            />
                                        )}

                                        {/* Tipo do fármaco */}
                                        {current?.enabled && (
                                            <Box mt={1}>
                                                {compareMode && compareTest ? (
                                                    <FieldCardCompare
                                                        label={t(group.typeLabelKey)}
                                                        value={current?.type}
                                                        compareValue={compare?.type}
                                                    />
                                                ) : (
                                                    <FieldCard
                                                        label={t(group.typeLabelKey)}
                                                        value={current?.type}
                                                    />
                                                )}
                                            </Box>
                                        )}
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </SectionCard>
                </Grid>

                {/* RESULTADOS */}
                <Grid size={12}>
                    <SectionCard title={t('stress.results')}>
                        <Grid container spacing={3}>
                            {RESULT_FIELDS.map((section) => (
                                <Grid key={section.title} size={{ xs: 12, md: 3 }}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 2,
                                            borderRadius: 2,
                                            border: '1px solid #e0e0e0'
                                        }}
                                    >
                                        <Typography
                                            variant="subtitle1"
                                            sx={{ mb: 2, fontWeight: 700 }}
                                        >
                                            {section.title}
                                        </Typography>

                                        <Grid container spacing={2}>
                                            {section.fields.map((field) => (
                                                <Grid key={field} size={12}>
                                                    {compareMode && compareTest ? (
                                                        <FieldCardCompare
                                                            label={field}
                                                            value={results[field]}
                                                            compareValue={
                                                                compareTest?.data
                                                                    ?.results?.[field]
                                                            }
                                                            background={getFieldColor(
                                                                field,
                                                                results[field]
                                                            )}
                                                            compareBackground={getFieldColor(
                                                                field,
                                                                compareTest?.data
                                                                    ?.results?.[field]
                                                            )}
                                                        />
                                                    ) : (
                                                        <FieldCard
                                                            label={field}
                                                            value={results[field]}
                                                            expanded={compareMode}
                                                            background={getFieldColor(
                                                                field,
                                                                results[field]
                                                            )}
                                                        />
                                                    )}
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    </SectionCard>
                </Grid>

                {/* RELATÓRIO */}
                <Grid size={12}>
                    <SectionCard title={t('stress.report')}>
                        {/* Atual */}
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                backgroundColor: '#fafafa'
                            }}
                        >
                            {compareMode && compareTest && (
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    {t('stress.currentReport')}
                                </Typography>
                            )}
                            <Typography whiteSpace="pre-line">{report}</Typography>
                        </Paper>

                        {/* Comparado */}
                        {compareMode && compareTest && (
                            <Paper
                                sx={{
                                    p: 2,
                                    mt: 2,
                                    borderRadius: 2,
                                    border: '2px solid #1976d2',
                                    backgroundColor: '#fafafa'
                                }}
                            >
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    {t('stress.reportSelected')}
                                </Typography>

                                <Typography whiteSpace="pre-line">
                                    {compareTest.report}
                                </Typography>
                            </Paper>
                        )}
                    </SectionCard>
                </Grid>

                {/* EXAME / PDF */}
                <Grid size={12}>
                    <SectionCard title={t('stress.pdfFile')}>
                        {file ? (
                            <Button
                                variant="contained"
                                startIcon={<PictureAsPdfIcon />}
                                onClick={() => window.open(file.url, '_blank')}
                            >
                                {t('stress.seePDF')}
                            </Button>
                        ) : (
                            <Typography color="text.secondary">
                                {t('stress.noPDF')}
                            </Typography>
                        )}
                    </SectionCard>
                </Grid>

                {/* AÇÕES */}
                <Grid
                    size={12}
                    sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}
                >
                    <Button sx={{ mr: 2 }} onClick={handleBack}>
                        {t('stress.back')}
                    </Button>

                    {!compareMode ? (
                        <Button
                            sx={{ mr: 2 }}
                            variant="contained"
                            onClick={() => setSelectCompareOpen(true)}
                        >
                            {t('stress.compareResults')}
                        </Button>
                    ) : (
                        <Button
                            sx={{ mr: 2 }}
                            color="error"
                            onClick={() => {
                                setCompareTest(null);
                                setSelectCompareOpen(false);
                            }}
                        >
                            {t('stress.cancelCompare')}
                        </Button>
                    )}
                </Grid>
            </Grid>
        </MainCard>
    );
};

export default ViewStressTest;
